// React imports
import { useRef, useState, useEffect, useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd';

// Generic imports
import _ from 'lodash';
import axios from 'axios';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

// MUI imports
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { grey, green, red, blue } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faUser, faCheck, faPlane, faShip, faTruck, faLock, faHandHoldingHeart, faUserPen } from '@fortawesome/pro-light-svg-icons';

// Local imports
import { calcChargeableWeight, calcWeightOrMeasurment } from './CalcWeight.js';
import { calcOfferSellAndCost } from './utils/opportunityFinancials.js';
import currencyToText from './CurrencyToText.js';
import readableId from './ReadableId.js';
import { statusToText } from './OpportunityRow.utils.js';
import OwnerFilter from './dashboard/components/OpportunitiesFilter.owner.js';

dayjs.extend(duration);
dayjs.extend(relativeTime);

const PIPELINE_TAG_PREFIX = 'Pipeline: ';

const COLUMNS = [
    { id: 'new-inquiry', title: 'New Inquiry', pipeline: 'New Inquiry', color: '#64748b' },
    { id: 'cost-research', title: 'Cost Research', pipeline: 'Cost Research', color: '#a16207' },
    { id: 'ready-for-sales', title: 'Ready for Sales', pipeline: 'Ready for Sales', color: '#0891b2' },
    { id: 'pending-customer', title: 'Pending Customer', pipeline: 'Pending Customer', color: '#7c3aed' },
    { id: 'follow-up', title: 'Follow-Up', pipeline: 'Follow-Up', color: '#db2777' },
    { id: 'closed', title: 'Closed', pipeline: null, readOnly: true, color: '#475569' },
];

const CLOSED_STATUSES = ['completed', 'lost', 'endcustomer-accepted', 'endcustomer-rejected'];

const DRAG_TYPE = 'OpportunityKanbanCard';

function getPipelineStage(opportunity) {
    const tag = (opportunity.tags || []).find(t => t.label?.startsWith(PIPELINE_TAG_PREFIX));
    return tag ? tag.label.replace(PIPELINE_TAG_PREFIX, '') : 'New Inquiry';
}

function isClosed(opportunity) {
    return CLOSED_STATUSES.includes(opportunity.status);
}

function findColumnForOpportunity(opportunity) {
    if (isClosed(opportunity)) return COLUMNS.find(c => c.id === 'closed');
    const stage = getPipelineStage(opportunity);
    return COLUMNS.find(c => c.pipeline === stage) || COLUMNS[0];
}

function FlowWelcomeKanban(props) {
    const { opportunities, accounts, onOpen, onUpdateOpportunity, accesstoken, org, me, exchangeRates } = props;
    const [maxCards, setMaxCards] = useState(_.fromPairs(COLUMNS.map(c => [c.id, 50])));
    const [saving, setSaving] = useState({});
    const [optimistic, setOptimistic] = useState({}); // opportunityId → pipeline stage name
    const kanbanRef = useRef(null);

    useEffect(() => { kanbanRef.current?.focus(); }, []);

    const onKanbanKeyDown = useCallback((e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            kanbanRef.current?.scrollBy({ left: e.key === 'ArrowRight' ? 300 : -300, behavior: 'smooth' });
        }
    }, []);

    async function onChangeStage({ opportunity, column }) {
        if (column.readOnly || !column.pipeline) return;
        const currentColumn = findColumnForOpportunity(opportunity);
        if (currentColumn.id === column.id) return;

        setOptimistic(o => ({ ...o, [opportunity.id]: column.pipeline }));
        setSaving(s => ({ ...s, [opportunity.id]: 'working' }));
        try {
            const tags = (opportunity.tags || []).filter(t => !t.label?.startsWith(PIPELINE_TAG_PREFIX));
            tags.push({ label: `${PIPELINE_TAG_PREFIX}${column.pipeline}` });
            const { data: updated } = await axios.put(
                `/api/opportunities/${opportunity.id}?organizationId=${opportunity.organizationId || org.id}&accesstoken=${accesstoken}`,
                { opportunity: { tags } }
            );
            onUpdateOpportunity({ opportunity: updated });
            setOptimistic(o => _.omit(o, opportunity.id));
            setSaving(s => ({ ...s, [opportunity.id]: 'success' }));
            await new Promise(res => setTimeout(res, 2000));
            setSaving(s => ({ ...s, [opportunity.id]: null }));
        } catch (e) {
            console.error('Kanban stage update failed', e);
            setOptimistic(o => _.omit(o, opportunity.id));
            setSaving(s => ({ ...s, [opportunity.id]: 'error' }));
        }
    }

    function effectiveColumn(o) {
        if (isClosed(o)) return COLUMNS.find(c => c.id === 'closed');
        if (o.id in optimistic) {
            return COLUMNS.find(c => c.pipeline === optimistic[o.id]) || COLUMNS[0];
        }
        return findColumnForOpportunity(o);
    }

    const cardsByColumn = COLUMNS.map(col => {
        const cards = _.orderBy(
            opportunities.filter(o => effectiveColumn(o)?.id === col.id),
            o => o.created,
            'desc'
        );
        return { col, cards };
    });

    return (
        <Box
            ref={kanbanRef}
            tabIndex={0}
            onKeyDown={onKanbanKeyDown}
            sx={{
                display: 'flex',
                mt: '12px',
                overflowX: 'auto',
                pb: '12px',
                outline: 'none',
                // The board runs wider than the window, and macOS keeps its scrollbar hidden until
                // something scrolls — which a trackpad does and a mouse wheel, on a board that only
                // scrolls sideways, does not. So there was nothing on screen to say the columns
                // continued, and nothing to drag to reach them. Draw the bar always.
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e1 transparent',
                '&::-webkit-scrollbar': { height: '10px', WebkitAppearance: 'none' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                    background: '#cbd5e1',
                    borderRadius: '5px',
                    border: '2px solid transparent',
                    backgroundClip: 'content-box',
                },
                '&::-webkit-scrollbar-thumb:hover': { background: '#94a3b8', backgroundClip: 'content-box' },
            }}
        >
            {cardsByColumn.map(({ col, cards }) => (
                <Column
                    key={col.id}
                    col={col}
                    cards={cards}
                    accounts={accounts}
                    onOpen={onOpen}
                    onDrop={(opportunity) => onChangeStage({ opportunity, column: col })}
                    saving={saving}
                    max={maxCards[col.id]}
                    onShowMore={() => setMaxCards(m => ({ ...m, [col.id]: m[col.id] + 50 }))}
                    org={org}
                    me={me}
                    accesstoken={accesstoken}
                    onUpdateOpportunity={onUpdateOpportunity}
                    exchangeRates={exchangeRates}
                />
            ))}
        </Box>
    );
}

function Column({ col, cards, accounts, onOpen, onDrop, saving, max, onShowMore, org, me, accesstoken, onUpdateOpportunity, exchangeRates }) {
    const visible = _.slice(cards, 0, max);
    const hasMore = cards.length > max;
    const _onDrop = useRef(onDrop);
    _onDrop.current = onDrop;
    const [isDropping, setIsDropping] = useState(false);
    const _setIsDropping = useRef(setIsDropping);
    _setIsDropping.current = setIsDropping;
    const first = useRef(false);

    const [, drop] = useDrop(() => ({
        accept: [DRAG_TYPE],
        canDrop: (item) => {
            if (col.readOnly) return false;
            const currentColumn = findColumnForOpportunity(item.opportunity);
            return currentColumn.id !== col.id;
        },
        hover: (item, monitor) => {
            if (first.current) return;
            first.current = true;
            if (monitor.isOver({ shallow: true })) {
                _setIsDropping.current(monitor.canDrop(item, monitor));
            }
            function isOver() {
                if (monitor.isOver()) {
                    if (monitor.isOver({ shallow: true })) {
                        _setIsDropping.current(monitor.canDrop(item, monitor));
                    } else {
                        _setIsDropping.current(false);
                    }
                    setTimeout(isOver, 100);
                } else {
                    first.current = false;
                    _setIsDropping.current(false);
                }
            }
            setTimeout(isOver, 100);
        },
        drop: (item) => {
            _onDrop.current(item.opportunity);
        },
    }), [col.id, col.readOnly]);

    return (
        <Box ref={drop} sx={{ display: 'flex', flexDirection: 'column', width: '290px', minWidth: '290px', maxWidth: '290px', mr: '6px', border: '1px solid #eee', p: '6px', background: isDropping ? 'rgba(59, 130, 246, 0.06)' : 'transparent', borderRadius: '8px' }}>
            <Box sx={{ background: col.color, borderRadius: '8px', p: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {col.readOnly && <Tooltip title='This column is read-only. Opportunities move here when marked as Won or Lost. Use the Reopen button on a card to move it back to the pipeline.' arrow placement='top'><span><FontAwesomeIcon icon={faLock} style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', cursor: 'help' }} /></span></Tooltip>}
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>{col.title}</Typography>
                </Box>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '10px', px: '8px', py: '1px' }}>{cards.length}</Typography>
            </Box>
            {visible.map(opportunity => (
                <Card key={opportunity.id} opportunity={opportunity} accounts={accounts} onOpen={onOpen} saving={saving[opportunity.id]} org={org} me={me} accesstoken={accesstoken} onUpdateOpportunity={onUpdateOpportunity} exchangeRates={exchangeRates} />
            ))}
            {hasMore && (
                <Button sx={{ width: '100%', mt: 1, textTransform: 'none' }} variant='text' onClick={onShowMore}>Show more...</Button>
            )}
        </Box>
    );
}

function Card({ opportunity, accounts, onOpen, saving, org, me, accesstoken, onUpdateOpportunity, exchangeRates }) {
    const theme = useTheme();
    const _opportunity = useRef(opportunity);
    _opportunity.current = opportunity;
    const [isDragging, setIsDragging] = useState(false);
    const _setIsDragging = useRef(setIsDragging);
    _setIsDragging.current = setIsDragging;

    const closed = isClosed(opportunity);
    const [, drag] = useDrag(() => ({
        type: DRAG_TYPE,
        item: () => {
            _setIsDragging.current(true);
            return { opportunity: _opportunity.current };
        },
        collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
        end: () => { _setIsDragging.current(false); },
        canDrag: () => !closed,
    }));

    const [idCopied, setIdCopied] = useState(false);
    const [ownerAnchor, setOwnerAnchor] = useState(null);
    const [changeOwnerDialog, setChangeOwnerDialog] = useState(false);
    const [newOwner, setNewOwner] = useState(null);
    const [ownerSaving, setOwnerSaving] = useState(false);

    const isOwner = opportunity.owner === me?.email;

    async function onTakeOwnership() {
        setOwnerAnchor(null);
        setOwnerSaving(true);
        try {
            const { data } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${opportunity.organizationId}&accesstoken=${accesstoken}`, {
                owner: me.email,
            });
            onUpdateOpportunity({ opportunity: { ...opportunity, ...data } });
        } catch (e) {
            console.log('Error taking ownership:', e);
        }
        setOwnerSaving(false);
    }

    async function onChangeOwnership() {
        setOwnerSaving(true);
        try {
            const { data } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${opportunity.organizationId}&accesstoken=${accesstoken}`, {
                owner: newOwner,
            });
            onUpdateOpportunity({ opportunity: { ...opportunity, ...data } });
            setChangeOwnerDialog(false);
        } catch (e) {
            console.log('Error changing ownership:', e);
        }
        setOwnerSaving(false);
    }

    const account = (accounts || []).find(a => a && a.id === opportunity.account?.id);
    const accountName = account?.name || opportunity.account?.email || opportunity.account?.name || 'No account';

    const routes = (() => {
        if (opportunity.type === 'one-off' || opportunity.type === 'one-off-email' || opportunity.type === 'one-off-free-text') return opportunity.routes;
        if (opportunity.type === 'contract') return _.uniqBy((opportunity.offers || []).map(o => o.routes?.[0]).filter(Boolean), o => JSON.stringify(o));
        return [];
    })();
    const firstRoute = routes?.[0];

    const typeLabel = opportunity.type === 'contract' ? 'Contract' : 'Quote';
    const ownerShort = (opportunity.owner || '').split('@')[0];
    const createdByShort = (opportunity.createdBy || '').split('@')[0];
    const quoteStatus = statusToText({ opportunity }) || 'Draft';

    // Cargo sums
    const sums = _(opportunity.products || [])
        .map((p) => {
            const CW = calcChargeableWeight({
                product: p, transportationMethod: opportunity.transportationMethod, allProducts: opportunity.products, round: org?.airRoundChargeableWeight
            });
            if (CW) {
                let valInKg = CW.val;
                if (CW.postfix === 'lbs') valInKg = CW.val * 0.453592;
                return { type: 'CW', value: valInKg, postfix: 'kg' };
            }
            const WM = calcWeightOrMeasurment({
                product: p, transportationMethod: opportunity.transportationMethod, allProducts: opportunity.products
            });
            if (WM) return { type: 'W/M', value: WM.val, postfix: WM.postfix };
            // Products whose type doesn't match the opportunity's transportation method
            // (e.g. an Air freight product on a sea quote) reach this fallback. The
            // unified 'Air freight'/'LCL' types keep their quantity inside
            // calculations[], so p.quantity is undefined — skip those rather than
            // counting them as containers.
            if (_.isUndefined(p.quantity)) return null;
            return { type: 'Containers', value: p.quantity, postfix: '' };
        })
        .compact()
        .groupBy(s => `${s.type}|${s.postfix}`)
        // sumBy returns undefined when every value in the group is undefined (e.g. a
        // product with no quantity), which would blow up on toLocaleString below.
        .mapValues(v => _.sumBy(v, s => s.value || 0))
        .value();

    const sumsText = _.map(sums, (v, k) => {
        const [type, postfix] = k.split('|');
        return `${type}: ${v.toLocaleString()}${postfix ? ' ' + postfix : ''}`;
    }).join(' | ');

    // Profit data
    const profitData = (() => {
        if (!exchangeRates || !org) return null;
        const cur = org.defaults?.currency || 'USD';
        const selectedOffer = opportunity.offers?.find(o => opportunity.selected?.includes(o.id)) || opportunity.offers?.[0];
        if (!selectedOffer) return null;
        const { sell, cost, gp } = calcOfferSellAndCost({ offer: selectedOffer, opportunity, exchangeRates, organization: org });
        const margin = sell > 0 ? (gp / sell) * 100 : 0;
        if (sell === 0 && cost === 0) return null;
        if (!isFinite(sell) || !isFinite(cost) || isNaN(sell) || isNaN(cost)) return null;
        const fmt = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        return { sell, cost, gp, margin, cur, fmt, gpPositive: gp >= 0 };
    })();

    // Quote status chip color
    const statusChipColor = (() => {
        if (opportunity.status === 'completed' || opportunity.status === 'endcustomer-accepted') return { bg: green[50], color: green[800] };
        if (opportunity.status === 'lost' || opportunity.status === 'endcustomer-rejected') return { bg: red[50], color: red[800] };
        if (opportunity.status === 'sent-to-endcustomer' || opportunity.status === 'opened-by-customer') return { bg: blue[50], color: blue[800] };
        return { bg: grey[100], color: grey[700] };
    })();

    return (
        <Paper
            ref={drag}
            elevation={0}
            sx={{
                mt: 1, p: 1.5, width: '278px', borderRadius: '8px',
                border: `1px solid ${theme.palette.border1 || '#eee'}`,
                cursor: closed ? 'default' : 'pointer',
                opacity: closed ? 0.7 : isDragging ? 0.3 : 1,
                '&:hover': { boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
                ...(isClosed(opportunity) && opportunity.status === 'completed' ? { borderLeft: `3px solid ${green[500]}` } : {}),
                ...(isClosed(opportunity) && opportunity.status === 'lost' ? { borderLeft: `3px solid ${red[400]}` } : {}),
            }}
            onClick={() => onOpen({ opportunity })}
        >
            {/* Header: account name + open button */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                <Typography sx={{ fontWeight: 700, flex: 1, fontSize: '13px', lineHeight: 1.3 }}>{accountName}</Typography>
                <IconButton size='small' onClick={(e) => { e.stopPropagation(); onOpen({ opportunity, newWindow: true }); }} sx={{ color: grey[400], '&:hover': { color: grey[700] }, ml: 0.5 }}>
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '11px' }} />
                </IconButton>
            </Box>

            {/* Route */}
            {firstRoute && (
                <Typography sx={{ fontSize: '11px', color: '#475569', mb: 0.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {opportunity.transportationMethod === 'air' && <FontAwesomeIcon icon={faPlane} style={{ marginRight: 4 }} />}
                    {opportunity.transportationMethod === 'sea' && <FontAwesomeIcon icon={faShip} style={{ marginRight: 4 }} />}
                    {opportunity.transportationMethod === 'land' && <FontAwesomeIcon icon={faTruck} style={{ marginRight: 4 }} />}
                    {firstRoute.source?.text || 'Everywhere'} → {firstRoute.destination?.text || 'Everywhere'}
                    {routes.length > 1 ? ` +${routes.length - 1}` : ''}
                </Typography>
            )}

            {/* Cargo sums */}
            {sumsText && (
                <Typography sx={{ fontSize: '10px', color: '#64748b', mt: 0.3 }}>
                    <Tooltip title={idCopied ? 'Copied!' : 'Click to copy ID'} arrow placement='top'>
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(readableId(opportunity));
                                setIdCopied(true);
                                setTimeout(() => setIdCopied(false), 2000);
                            }}
                            style={{ cursor: 'pointer', borderRadius: '3px', padding: '0 2px', transition: 'background 0.15s' }}
                            onMouseEnter={(e) => e.target.style.background = '#e2e8f0'}
                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >
                            {readableId(opportunity)}
                        </span>
                    </Tooltip>
                    {' | '}{sumsText}
                </Typography>
            )}

            {/* Type + Quote Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#3b82f6' }}>{typeLabel}</Typography>
                <Chip
                    label={quoteStatus}
                    size='small'
                    sx={{ fontSize: '9px', height: 18, fontWeight: 600, backgroundColor: statusChipColor.bg, color: statusChipColor.color }}
                />
            </Box>

            {/* Financials */}
            {profitData && (
                <Box sx={{ mt: 0.5, p: '3px 0' }}>
                    <Typography sx={{ fontSize: '10px', color: '#64748b' }}>
                        Sell: <b>{currencyToText(profitData.cur, true, profitData.fmt.format(profitData.sell))}</b> | Cost: <b>{currencyToText(profitData.cur, true, profitData.fmt.format(profitData.cost))}</b>
                    </Typography>
                    <Typography sx={{ fontSize: '10px', color: '#64748b' }}>
                        GP: <b style={{ color: profitData.gpPositive ? '#2e7d32' : '#d32f2f' }}>{currencyToText(profitData.cur, true, profitData.fmt.format(profitData.gp))}</b> | Margin: <b style={{ color: profitData.gpPositive ? '#2e7d32' : '#d32f2f' }}>{profitData.margin.toFixed(1)}%</b>
                    </Typography>
                </Box>
            )}

            {/* Meta: created, owner */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>
                    {opportunity.created ? dayjs.duration((opportunity.created - Date.now()) / 1000, 'seconds').humanize(true) : ''}
                </Typography>
                {ownerShort && (
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', '&:hover': { color: '#64748b' } }}
                        onClick={(e) => { e.stopPropagation(); setOwnerAnchor(e.currentTarget); }}
                    >
                        <FontAwesomeIcon icon={faUser} style={{ fontSize: '9px', color: ownerSaving ? '#3b82f6' : '#94a3b8' }} />
                        <Typography sx={{ fontSize: '10px', color: ownerSaving ? '#3b82f6' : '#94a3b8' }}>{ownerSaving ? 'Saving...' : ownerShort}</Typography>
                    </Box>
                )}
                <Popover
                    open={Boolean(ownerAnchor)}
                    anchorEl={ownerAnchor}
                    onClose={() => setOwnerAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <MenuList dense sx={{ py: 0.5 }}>
                        {!isOwner && (
                            <MenuItem onClick={onTakeOwnership}>
                                <ListItemIcon><FontAwesomeIcon icon={faHandHoldingHeart} style={{ fontSize: '13px' }} /></ListItemIcon>
                                <ListItemText><Typography sx={{ fontSize: '12px' }}>Take Ownership</Typography></ListItemText>
                            </MenuItem>
                        )}
                        <MenuItem onClick={() => { setOwnerAnchor(null); setNewOwner(org.users[0]?.email); setChangeOwnerDialog(true); }}>
                            <ListItemIcon><FontAwesomeIcon icon={faUserPen} style={{ fontSize: '13px' }} /></ListItemIcon>
                            <ListItemText><Typography sx={{ fontSize: '12px' }}>{isOwner ? 'Change Ownership' : 'Reassign Ownership'}</Typography></ListItemText>
                        </MenuItem>
                    </MenuList>
                </Popover>
                {changeOwnerDialog && (
                    <Dialog open={true} onClose={() => setChangeOwnerDialog(false)} onClick={(e) => e.stopPropagation()}>
                        <DialogTitle>Change Ownership</DialogTitle>
                        <DialogContent>
                            <Typography variant='body2' sx={{ mt: 1, mb: 2 }}>
                                Select the new owner for this opportunity:
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                <OwnerFilter
                                    org={org}
                                    filter={{ members: [newOwner] }}
                                    setFilter={(filter) => setNewOwner(filter.members[0])}
                                    me={me}
                                    singlePick={true}
                                    disabled={false}
                                    teamLevel={false}
                                    noSelectionText={null}
                                    noMarginRight={false}
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setChangeOwnerDialog(false)} disabled={ownerSaving}>Cancel</Button>
                            <Button onClick={onChangeOwnership} disabled={ownerSaving}>
                                {ownerSaving ? <CircularProgress size={14} /> : 'Change Ownership'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
            </Box>


            {/* Saving indicators */}
            {saving === 'working' && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.5 }}>
                    <Typography variant='caption' sx={{ mr: '6px' }}>Saving...</Typography>
                    <CircularProgress size={14} sx={{ opacity: 0.5 }} />
                </Box>
            )}
            {saving === 'success' && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.5 }}>
                    <FontAwesomeIcon icon={faCheck} color={green[500]} />
                </Box>
            )}
            {saving === 'error' && (
                <Alert severity='error' sx={{ mt: 0.5, fontSize: '10px', py: 0 }}>Saving failed.</Alert>
            )}
        </Paper>
    );
}

export default FlowWelcomeKanban;
